import React, { useState, useContext, useEffect, useRef } from 'react';
import { setActivateIdle } from 'redux/session/actions';
import {
  currentUser,
  selectorAlertNotificationShowHide
} from 'redux/session/selectors';
import { selectCurrentTeamId } from 'redux/team/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import {
  Button,
  InlineNotification,
  ToastNotification
} from 'carbon-components-react';
import { Close16 } from '@carbon/icons-react';
import { LOCATION_TYPES_CODE } from 'types';
import AutoRenewForm from './forms/autoRenew.form';
import MapLocationForm from './forms/mapLocation.form';
import JobDetailForm from './forms/jobDetail.form';
import AuthorityReferenceForm from './forms/authorityRefer.form';
import SuccessfulLodgedForm from './forms/successfulLodged.form';
import ShapeDetailForm from './forms/shapeDetail.form';
import {
  EnquiryShape,
  LodgeEnqyiryInitialValues
} from '../../localStore/types';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import {
  lodgeNewEnquiryStart,
  setMultipleDrawMode
} from '../../localStore/actions';
import {
  isObjectEmpty,
  getNewDate,
  isAfterTodayDate,
  getDateAfterDays
} from 'utilities/helper.util';
import { workPlaceLocItems } from 'app/contants';

const NEXT_28_DAYS_DATE: any = getDateAfterDays(28);

const convertLocationInRoad = (loc: string) => {
  switch (loc) {
    case 'Nature Strip':
      return 'Berm';
    case 'Road':
      return 'CarriageWay';
    default:
      return loc;
  }
};

const convertEnquiryToValues = (
  enquiry: any,
  skipUserRefer: boolean = false,
  authorityList?: any[],
  authorityTypeList?: any[]
) => {
  const jobStartDate = isAfterTodayDate(enquiry.start_at)
    ? getNewDate(enquiry.start_at)
    : '';
  const jobEndDate = isAfterTodayDate(enquiry.end_at)
    ? getNewDate(enquiry.end_at)
    : '';
  const locationInRoad = enquiry.location_in_road
    ? enquiry.location_in_road.split(',')
    : [];
  const locations = locationInRoad.map((loc: string) => {
    const locValue = convertLocationInRoad(loc);
    return workPlaceLocItems.find((i) => i.value === locValue)?.id;
  });

  if (enquiry.workplace_id === 'B' || enquiry.workplace_id === 'P') {
    locations.push('private');
  }

  if (
    enquiry.location_of_workplace &&
    typeof enquiry.location_of_workplace === 'string' &&
    enquiry.location_of_workplace.toLowerCase().includes('private') &&
    !locations.includes('private')
  ) {
    locations.push('private');
  }

  let activityId: string[] = [];
  if (Array.isArray(enquiry.onsite_activity)) {
    activityId = enquiry.onsite_activity.map(String);
  } else if (typeof enquiry.onsite_activity === 'string') {
    activityId = enquiry.onsite_activity
      .split(',')
      .map((id: string) => id.trim())
      .filter(Boolean);
  } else if (typeof enquiry.onsite_activity === 'number') {
    activityId = [String(enquiry.onsite_activity)];
  }

  let authorityType = String(enquiry.on_behalf_id);
  let authorityId = enquiry.authority_id ? String(enquiry.authority_id) : '';
  if (
    (!authorityType || authorityType === 'null') &&
    (enquiry.working_on_behalf === 'Local Government' ||
      enquiry.working_on_behalf === 'Utility' ||
      enquiry.working_on_behalf === 'Private' ||
      enquiry.working_on_behalf === 'Other')
  ) {
    const targetLabel = enquiry.working_on_behalf;
    let fallbackId = '';
    if (targetLabel === 'Local Government') {
      fallbackId = '1';
      authorityType = '1';
      if (enquiry.authority_name && authorityList && authorityList.length > 0) {
        const match = authorityList.find(
          (a: any) =>
            a.authorityTypeId === '1' &&
            (a.label === enquiry.authority_name ||
              a.Name === enquiry.authority_name)
        );
        if (match) {
          authorityId = String(match.value);
        }
      }
    } else if (targetLabel === 'Utility') {
      fallbackId = '2';
      authorityType = '2';
      if (enquiry.authority_name && authorityList && authorityList.length > 0) {
        const match = authorityList.find(
          (a: any) =>
            a.authorityTypeId === '2' &&
            (a.label === enquiry.authority_name ||
              a.Name === enquiry.authority_name)
        );
        if (match) {
          authorityId = String(match.value);
        }
      }
    } else if (targetLabel === 'Private') {
      fallbackId = '3';
    } else if (targetLabel === 'Other') {
      fallbackId = '4';
    }
    let targetOption = null;
    if (authorityList && authorityList.length > 0) {
      targetOption = authorityList.find(
        (a: any) => a.label === targetLabel || a.Name === targetLabel
      );
    }
    if (!targetOption && authorityTypeList && authorityTypeList.length > 0) {
      targetOption = authorityTypeList.find(
        (a: any) => a.label === targetLabel || a.Name === targetLabel
      );
    }
    if (targetOption) {
      authorityType = String(targetOption.value);
    } else {
      authorityType = fallbackId;
    }
  }
  if (authorityList && (authorityType === '1' || authorityType === '2')) {
    const found = authorityList.find(
      (a) =>
        a.authorityTypeId === authorityType &&
        (a.value === authorityId || a.value === Number(authorityId))
    );
    if (!found) {
      authorityType = '';
      authorityId = '';
    }
  }

  let otherAuthorityName = enquiry.other_authority_name || '';
  if (
    (authorityType === '4' || enquiry.working_on_behalf === 'Other') &&
    !otherAuthorityName &&
    enquiry.authority_name
  ) {
    otherAuthorityName = enquiry.authority_name;
  }

  return {
    userReference: !skipUserRefer ? enquiry.user_reference || '' : undefined,
    jobStartDate,
    jobEndDate,
    notes: enquiry.notes_description_of_works || '',
    enquiryType: enquiry.job_purpose,
    activityId,
    authorityType,
    authorityId,
    locations,
    otherAuthorityName
  };
};

const EnquiryFormPanel: React.FC<Props> = ({
  formInitialValues,
  formValuesRefs,
  appliedDraftToForm,
  setFormChanged,
  jobIdInNotes,
  selectedShapeInMap,
  setSelectedShapeInMap,
  setShapeDetailFormTouched,
  cancelEnquiryHandler,
  completeEnquiryHandler
}) => {
  const addressLine1InputRef = useRef<HTMLInputElement>(null);
  const IdleClearingInterval = useRef<NodeJS.Timeout>();
  const dispatch = useDispatch();
  const user = useSelector(currentUser);
  const alertNotificationToggle = useSelector(
    selectorAlertNotificationShowHide
  );
  const teamId = useSelector(selectCurrentTeamId);
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showAutoRenew, setShowAutoRenew] = useState<boolean>(false);
  const [contactInNote, setContactInNote] = useState<string>('');
  const [redrawCount, setRedrawCount] = useState(0);

  const {
    designAct,
    excavationAct,
    shapes,
    authorityTypeList,
    authorityList,
    duplicateEnquiry,
    bulkCreating,
    pendingEnquiriesAmount,
    enquiries,
    draftTouched,
    restriction,
    lastEnquiry,
    uploadedShapesUserReferences,
    useUploadedShapeUserReference
  } = localStore;
  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    setValues
  } = useFormik({
    initialValues: formValuesRefs.current || formInitialValues,
    onSubmit: (values: LodgeEnqyiryInitialValues) => {
      const [locCode, locName, locInRoad] = getWorkPlaceLocation(
        values.locations
      );

      const getActivityNames = (
        activityIds: string[] | string,
        list: any[]
      ) => {
        if (!activityIds) return '';
        const ids = Array.isArray(activityIds) ? activityIds : [activityIds];
        return ids
          .map((id) => {
            const found = list.find((item: any) => item.value === id);
            return found ? found.label : id;
          })
          .join(',');
      };

      const activityList =
        values.enquiryType === 'Design' ? designAct : excavationAct;
      const activityIdsRaw = Array.isArray(values.activityId)
        ? values.activityId
        : [values.activityId];
      const activityIds = activityIdsRaw
        .filter((id) => typeof id === 'string' && !!id)
        .map((id) => String(id));

      const singlePayload = {
        OtherAuthorityName: values.otherAuthorityName,
        project_id: teamId || null,
        EnquiryDate: getNewDate().format(),
        ActivityId: values.activityId,
        onsite_activity: activityIds.join(','),
        ActivityName: getActivityNames(activityIds, activityList),
        AdditionalNotes: values.notes || null,
        CommencesOn: getNewDate(values.jobStartDate).startOf('day').format(),
        CompletesOn: getNewDate(values.jobEndDate).startOf('day').format(),
        LocationAddress:
          shapes.length === 1
            ? {
                Country: shapes[0].address?.country || 'AUS',
                Postcode: values.addressPostcode,
                State: values.addressState,
                Street: values.addressLine1,
                Suburb: values.addressLocality
              }
            : null,
        LocationInRoad: { 'Enums.LocationInRoadType': locInRoad },
        PurposeId: values.enquiryType === 'Design' ? '1' : '0',
        PurposeName: values.enquiryType,
        SpatialObjectDescription:
          shapes.length === 1 ? shapes[0].geoJson : null,
        SpatialObjectProjection: 'WGS84',
        UserReference: values.userReference || values.addressLine1,
        WorkingOnBehalfOfId: values.authorityType,
        WorkingOnBehalfOfName: getOptionName(
          authorityTypeList || [],
          values.authorityType
        ),
        WorkingOnBehalfOfAuthorityId: values.authorityId || undefined,
        WorkplaceLocationCode: locCode,
        WorkplaceLocationValue: locName,
        map_zoom: null,
        authority_name: getOptionName(authorityList || [], values.authorityId),
        auto_renew: getNewDate(values.jobEndDate).isAfter(
          NEXT_28_DAYS_DATE,
          'date'
        )
          ? values.autoRenew
          : false,
        auto_renew_every: values.autoRenewEvery || null,
        auto_renew_until: values.autoRenewUntil
          ? getNewDate(values.autoRenewUntil).startOf('day').format()
          : null,
        bulk_id: shapes.length === 1 ? shapes[0].shapeId : null,
        duplicate_from: values.duplicateFrom || null
      };

      const shapesSubmit = values.retryFailed
        ? shapes.filter((s: EnquiryShape) => s.isFailedLodge)
        : shapes;

      const shapeswithRef = shapesSubmit.filter((s) => s.userReference);

      if (shapes.length > 1 && shapesSubmit.length < 0) {
        return;
      }

      const payload =
        shapes.length > 1
          ? shapesSubmit.map((s: EnquiryShape) => {
              const inx =
                !draftTouched && s.lastSeqNo
                  ? s.lastSeqNo
                  : shapes.findIndex((os) => s.uid === os.uid);
              return {
                ...singlePayload,
                LocationAddress: {
                  Country: s.address.country || 'AUS',
                  Postcode: s.address.postcode,
                  State: s.address.state,
                  Street: s.address.line1,
                  Suburb: s.address.locality
                },
                SpatialObjectDescription: s.geoJson,
                UserReference:
                  shapeswithRef.length === 0
                    ? values.userReference
                      ? `${values.userReference} ${inx + 1}`
                      : s.address.line1
                    : s.userReference || s.address.line1,
                bulk_id: s.shapeId,
                map_id: s.uid
              };
            })
          : singlePayload;

      localDispatch(lodgeNewEnquiryStart(payload));
    }
  });

  const getOptionName = (list: any[], id?: string) => {
    if (list?.length && id) {
      const selected = list.find(
        (a: any) => a.value.toString() === id.toString()
      );
      return selected.label;
    }
  };

  const getWorkPlaceLocation = (locations: string[]) => {
    const havePrivate = locations.includes('private');
    const locInRoad = locations
      .filter((l) => l !== 'private')
      .map((l) => {
        return workPlaceLocItems.find((i) => i.id === l)?.value;
      });

    const haveRoad = locInRoad.length > 0;
    const code = havePrivate && haveRoad ? 'B' : havePrivate ? 'P' : 'R';
    const name =
      havePrivate && haveRoad
        ? LOCATION_TYPES_CODE.BOTH
        : havePrivate
        ? LOCATION_TYPES_CODE.PRIVATE
        : LOCATION_TYPES_CODE.ROAD;

    return [code, name, locInRoad];
  };

  const copyFromLastEnquiry = (
    lastEnquiry: any,
    currentValues: LodgeEnqyiryInitialValues
  ) => {
    if (lastEnquiry) {
      const newValues = convertEnquiryToValues(
        lastEnquiry,
        shapes.length > 1,
        authorityList,
        authorityTypeList
      );
      setValues({
        ...currentValues,
        ...newValues
      });
      setFieldTouched('duplicateFrom', true);
    }
  };

  const refineAddress = (delay: boolean = false) => {
    setCurrentStep(0);
    if (addressLine1InputRef.current) {
      if (delay) {
        setTimeout(() => addressLine1InputRef.current?.focus(), 100);
      } else {
        addressLine1InputRef.current.focus();
      }
    }
  };

  const retryFailedEnquiries = () => {
    setFieldValue('retryFailed', true);
    handleSubmit();
  };

  const handleRedraw = () => {
    cancelEnquiryHandler(false);
    setRedrawCount((c) => c + 1);
  };

  useEffect(() => {
    (enquiries.length > 0 || bulkCreating) && setCurrentStep(3);
  }, [enquiries, bulkCreating]);

  useEffect(() => {
    if (bulkCreating) {
      IdleClearingInterval.current = setInterval(
        () => dispatch(setActivateIdle(true)),
        2000
      );
    } else {
      if (IdleClearingInterval.current) {
        clearInterval(IdleClearingInterval.current);
      }
    }
  }, [dispatch, bulkCreating]);

  useEffect(() => {
    formValuesRefs.current = values;
  }, [values, formValuesRefs]);

  useEffect(() => {
    if (!isObjectEmpty(touched) && !isSubmitting) {
      setFormChanged(true);
    }
  }, [values, touched, setFormChanged, isSubmitting]);

  useEffect(() => {
    const notes = `${user.first_name} ${user.last_name}\n${
      user.mobile_number ? `${user.mobile_number}\n` : ''
    }${user.reg_email}`;
    setContactInNote(notes);
  }, [user]);

  useEffect(() => {
    !appliedDraftToForm.current &&
      !duplicateEnquiry?.id &&
      setFieldValue('notes', `${jobIdInNotes}${contactInNote}`);
  }, [
    contactInNote,
    jobIdInNotes,
    setFieldValue,
    duplicateEnquiry,
    appliedDraftToForm
  ]);

  useEffect(() => {
    if (
      typeof uploadedShapesUserReferences === 'string' &&
      useUploadedShapeUserReference
    ) {
      setFieldValue('userReference', uploadedShapesUserReferences);
    }
  }, [
    uploadedShapesUserReferences,
    useUploadedShapeUserReference,
    setFieldValue
  ]);

  useEffect(() => {
    let newInitialValues = formInitialValues;
    if (duplicateEnquiry?.id) {
      const newValues = convertEnquiryToValues(
        duplicateEnquiry,
        shapes.length > 1,
        authorityList,
        authorityTypeList
      );
      newInitialValues = {
        ...newInitialValues,
        ...newValues,
        addressLine1:
          duplicateEnquiry.location_address?.Street ||
          duplicateEnquiry.location_address?.line1,
        addressLocality:
          duplicateEnquiry.location_address?.Suburb ||
          duplicateEnquiry.location_address?.locality,
        addressState:
          duplicateEnquiry.location_address?.State ||
          duplicateEnquiry.location_address?.state,
        addressPostcode:
          duplicateEnquiry.location_address?.Postcode ||
          duplicateEnquiry.location_address?.postcode,
        duplicateFrom: duplicateEnquiry.id
      };
      setValues(newInitialValues);
    }
  }, [
    duplicateEnquiry,
    setValues,
    formInitialValues,
    setFieldTouched,
    authorityList,
    authorityTypeList
  ]);

  useEffect(() => {
    if (localStore.draft && authorityList && authorityList.length > 0) {
      const draftData = localStore.draft.details?.data;
      if (draftData) {
        const newValues = convertEnquiryToValues(
          draftData,
          false,
          authorityList,
          authorityTypeList
        );
        setValues({
          ...formInitialValues,
          ...newValues,
          ...draftData
        });
      }
    }
  }, [localStore.draft, authorityList, authorityTypeList]);

  const forms = [
    <MapLocationForm
      key={0}
      touched={touched}
      errors={errors}
      values={values}
      handleChange={handleChange}
      addressLine1InputRef={addressLine1InputRef}
      handleBlur={handleBlur}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
      onRedraw={handleRedraw}
      onMultipleDraw={() => localDispatch(setMultipleDrawMode(true))}
      goNext={() => setCurrentStep(1)}
      refineAddress={refineAddress}
      copyBtnDisabled={!lastEnquiry}
      onClickCopyBtn={() => copyFromLastEnquiry(lastEnquiry, values)}
    />,
    <JobDetailForm
      key={1}
      touched={touched}
      errors={errors}
      values={values}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
      onClickAutoRenewBtn={() => setShowAutoRenew(true)}
      autoRenewBtnDisabled={
        !values.jobStartDate ||
        !values.jobEndDate ||
        !getNewDate(values.jobEndDate).isAfter(NEXT_28_DAYS_DATE, 'date')
      }
      goBack={() => setCurrentStep(0)}
      goNext={() => setCurrentStep(2)}
    />,
    <AuthorityReferenceForm
      key={2}
      touched={touched}
      errors={errors}
      values={values}
      setFieldValue={setFieldValue}
      handleChange={handleChange}
      handleBlur={handleBlur}
      setFieldTouched={setFieldTouched}
      goBack={() => setCurrentStep(1)}
      goNext={() => handleSubmit()}
      redrawCount={redrawCount}
    />,
    <SuccessfulLodgedForm
      key={3}
      completeEnquiryHandler={completeEnquiryHandler}
      retryFailedEnquiries={retryFailedEnquiries}
    />
  ];

  return (
    <div
      className={`${
        alertNotificationToggle ? 'noti-alert-notification-form-panel ' : ''
      }${
        restriction &&
        (!restriction.allow ||
          (restriction.total > 0 &&
            shapes.length + restriction.used > restriction.total))
          ? restriction.allow
            ? 'over-limit-alert allow '
            : 'over-limit-alert '
          : ''
      }enquiry-form-panel`}
    >
      <div
        className={`enquiry-form--header${
          shapes.length > 1 && !selectedShapeInMap ? ' multiple' : ''
        }${currentStep === 3 ? ' successful' : ''}`}
      >
        <div className="title">
          {selectedShapeInMap === undefined
            ? shapes.length > 1
              ? 'Bulk enquiry details'
              : 'Enquiry details'
            : 'Edit selected area details'}
        </div>
        <div className="controls">
          <Button
            kind="ghost"
            className="close-btn"
            disabled={
              pendingEnquiriesAmount !== undefined || !!selectedShapeInMap
            }
            onClick={() =>
              currentStep !== 3
                ? cancelEnquiryHandler(true)
                : completeEnquiryHandler()
            }
          >
            <Close16 />
          </Button>
        </div>
      </div>
      {shapes.length > 1 && currentStep !== 3 && !selectedShapeInMap && (
        <div className="enquiry-form--multi-noti">
          <InlineNotification
            title={`${shapes.length} Enquiries`}
            subtitle="(details apply to all)"
            kind="info"
            lowContrast
            hideCloseButton
          />
        </div>
      )}
      {restriction &&
        (!restriction.allow ||
          (restriction.total > 0 &&
            shapes.length + restriction.used > restriction.total)) && (
          <div className="enquiry-form--over-limit-noti">
            <ToastNotification
              kind="warning"
              title=""
              hideCloseButton={true}
              lowContrast={true}
              subtitle={
                <>
                  Your organisation has reached your enquiry limit.{' '}
                  {restriction.allow && (
                    <>
                      <b>
                        Remove{' '}
                        {shapes.length + restriction.used - restriction.total}{' '}
                        {shapes.length + restriction.used - restriction.total >
                        1
                          ? 'enquiries'
                          : 'enquiry'}
                      </b>{' '}
                      from the map to continue.
                    </>
                  )}
                  <br />
                  <br />
                  To increase your enquiry limit, contact your organisation
                  administrator.
                </>
              }
            />
          </div>
        )}
      <div
        className={`enquiry-form--body${shapes.length > 1 ? ' multiple' : ''}${
          currentStep === 3 ? ' successful' : ''
        }`}
      >
        {forms.map((enquiryForm, inx) => (
          <div
            key={inx}
            className={`enquiry-form-tab${
              inx === currentStep && !showAutoRenew && !selectedShapeInMap
                ? ' active'
                : ''
            }`}
          >
            {enquiryForm}
          </div>
        ))}
        {!!selectedShapeInMap && (
          <div className="enquiry-form-tab active">
            <ShapeDetailForm
              selectedShapeId={selectedShapeInMap}
              setShapeDetailFormTouched={setShapeDetailFormTouched}
              onClose={() => setSelectedShapeInMap(undefined)}
            />
          </div>
        )}
        {showAutoRenew && selectedShapeInMap === undefined && (
          <div className="enquiry-form-tab active">
            <AutoRenewForm
              mainValues={values}
              setMainFieldValue={setFieldValue}
              onClose={() => setShowAutoRenew(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  formInitialValues: LodgeEnqyiryInitialValues;
  formValuesRefs: React.MutableRefObject<LodgeEnqyiryInitialValues | undefined>;
  appliedDraftToForm: React.MutableRefObject<boolean>;
  setFormChanged: Function;
  jobIdInNotes: string;
  selectedShapeInMap?: number;
  setSelectedShapeInMap: Function;
  setShapeDetailFormTouched: Function;
  cancelEnquiryHandler: Function;
  completeEnquiryHandler: Function;
};

export default React.memo(EnquiryFormPanel);
